import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CATEGORY_LABELS, CATEGORY_ORDER } from "../lib/categories";
import { RotateCcw, Calendar, Package, Sparkles, Lock } from "lucide-react";
import FlavorQuickAdd, { FLAVOR_ADDABLE_CATEGORIES } from "./FlavorQuickAdd";

const PACKAGING_NOTE = "Prices vary and will be given upon order request. Please allow 3–4 months for delivery.";

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function fmtCurrency(n) {
  return `$${(n || 0).toFixed(2)}`;
}

/**
 * Faithful digital twin of the printed Rice Village order form.
 *
 * Layout (mobile/iPad: stacked; desktop: form left, sticky total right):
 *   Header bar         ← pickup date + customer + one-tap repeat
 *   ── for each product category (in PDF order) ──
 *     Section title    (e.g. "MACARONs · 70 per box · $91.00")
 *     Item rows        (flavor · qty input · line total)
 *     Section subtotal
 *   Packaging Re-order (qty only — no prices)
 *   Notes textarea
 *   Submit
 *
 * Grand total panel is sticky on desktop.
 */
export default function FranchiseOrderForm() {
  const { user } = useAuth();
  // Customer/destination is bound to the franchisee's franchise — they can't change it.
  // Admins (who can also use this form for off-the-books orders) get a free-text field.
  const defaultCustomer = user?.franchise_name || "";
  const isLockedCustomer = user?.role === "franchisee" && !!defaultCustomer;

  const [items, setItems] = useState([]);
  const [pickupDate, setPickupDate] = useState(todayISO());
  const [customer, setCustomer] = useState(defaultCustomer);
  const [notes, setNotes] = useState("");
  // For manual admin orders — free-form "where the order is coming from"
  // (e.g. "Phone-in · Alice Chen", "Walk-in", "Instagram DM"). Franchisees
  // don't see this; their orders always come from their own franchise.
  const [orderSource, setOrderSource] = useState("");
  // Admin-only: which kitchen fulfills this manual order. Employees in that
  // kitchen are the ones who will see the order after it's approved.
  const [fulfillmentLocationId, setFulfillmentLocationId] = useState("");
  const [locations, setLocations] = useState([]);
  // qty map: { [item_id]: quantity }
  const [qty, setQty] = useState({});
  // packaging qty map: { [packaging_item_name]: quantity }
  const [pkgQty, setPkgQty] = useState({});
  const [lastOrder, setLastOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const refreshItems = () =>
    api.get("/inventory/order-catalog").then((r) => {
      const seen = new Set();
      setItems(r.data.filter((it) => (seen.has(it.id) ? false : seen.add(it.id))));
    });

  useEffect(() => {
    // Franchisees only see items marked available (seasonal toggle respected)
    refreshItems();
    api.get("/orders?limit=1").then((r) => setLastOrder(r.data[0] || null));
    if (user?.role === "admin") {
      api.get("/locations").then((r) => setLocations(r.data)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Group items by category, in PDF order. Flavor-addable categories are
  // always rendered — even when the franchisee has 0 available items in
  // them — so the "+ Add Flavor" strip is always reachable from the order
  // form (otherwise a franchisee could never add their first seasonal /
  // gelato flavor through the order form).
  const grouped = useMemo(() => {
    const map = {};
    for (const it of items) {
      if (!map[it.category]) map[it.category] = [];
      map[it.category].push(it);
    }
    return CATEGORY_ORDER
      .filter((c) => c !== "packaging"
        && (map[c]?.length || FLAVOR_ADDABLE_CATEGORIES.has(c)))
      .map((c) => ({ category: c, label: CATEGORY_LABELS[c], items: map[c] || [] }));
  }, [items]);

  const packagingItems = useMemo(
    () => items.filter((i) => i.category === "packaging"),
    [items],
  );

  // Section subtotals + grand total
  const sectionTotals = useMemo(() => {
    const totals = {};
    let grand = 0;
    for (const g of grouped) {
      let s = 0;
      for (const it of g.items) {
        const q = qty[it.id] || 0;
        s += (it.price || 0) * q;
      }
      totals[g.category] = s;
      grand += s;
    }
    return { sections: totals, grand };
  }, [grouped, qty]);

  const linesCount = useMemo(
    () => Object.values(qty).filter((q) => q > 0).length
       + Object.values(pkgQty).filter((q) => q > 0).length,
    [qty, pkgQty],
  );

  const duplicateLast = () => {
    if (!lastOrder) return;
    const newQty = {};
    for (const it of lastOrder.items || []) newQty[it.item_id] = it.quantity;
    setQty(newQty);
    const newPkg = {};
    for (const p of lastOrder.packaging_items || []) newPkg[p.name] = p.quantity;
    setPkgQty(newPkg);
    // Don't overwrite the locked customer for franchisees
    if (!isLockedCustomer) setCustomer(lastOrder.customer || "");
    setNotes(`Repeat of ${lastOrder.order_no}`);
    toast.success(`Duplicated ${lastOrder.order_no}`, { description: "Adjust quantities below before placing." });
  };

  const submit = async () => {
    setErr("");
    if (!customer.trim()) return setErr("Customer / destination is required.");
    if (!pickupDate) return setErr("Pickup date is required.");
    const orderItems = [];
    for (const g of grouped) {
      for (const it of g.items) {
        const q = qty[it.id] || 0;
        if (q > 0) {
          orderItems.push({ item_id: it.id, name: it.name, quantity: q, category: it.category });
        }
      }
    }
    const packaging_items = packagingItems
      .filter((p) => (pkgQty[p.name] || 0) > 0)
      .map((p) => ({ name: p.name, quantity: pkgQty[p.name] }));

    if (!orderItems.length && !packaging_items.length) {
      return setErr("Add at least one item or packaging request before submitting.");
    }
    // Empty product order requires a real product line, not just packaging
    if (!orderItems.length) {
      return setErr("Add at least one product line. Packaging can only accompany an order.");
    }

    setBusy(true);
    try {
      const { data } = await api.post("/orders", {
        items: orderItems,
        customer,
        notes,
        pickup_date: pickupDate,
        packaging_items,
        order_source: orderSource || null,
        fulfillment_location_id: fulfillmentLocationId || null,
      });
      toast.success("Order submitted", { description: `${data.order_no} sent to the kitchen.` });
      setQty({}); setPkgQty({}); setNotes("");
      if (!isLockedCustomer) setCustomer("");
      setPickupDate(todayISO());
      setOrderSource("");
      setFulfillmentLocationId("");
      const fresh = await api.get("/orders?limit=1");
      setLastOrder(fresh.data[0] || null);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-[1fr_360px] gap-6">
      {/* ───── Left: form ───── */}
      <div className="space-y-6" data-testid="franchise-order-form">

        {/* Header card */}
        <div className="luxury-card p-5 sm:p-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="label-eyebrow mb-2 block inline-flex items-center gap-1.5">
                {isLockedCustomer && <Lock className="h-3 w-3" />}
                Customer / Destination
              </label>
              {isLockedCustomer ? (
                <div
                  className="h-12 w-full rounded-xl border border-input bg-secondary/40 px-3 text-sm flex items-center font-medium"
                  data-testid="order-customer-locked"
                  aria-label="Customer locked to your franchise"
                >
                  {customer}
                </div>
              ) : (
                <input
                  value={customer}
                  onChange={(e) => setCustomer(e.target.value)}
                  placeholder="Rice Village"
                  data-testid="order-customer"
                  className="h-12 w-full rounded-xl border border-input bg-card px-3 text-sm"
                />
              )}
            </div>
            <div>
              <label className="label-eyebrow mb-2 block inline-flex items-center gap-1.5">
                <Calendar className="h-3 w-3" /> Requested Pickup Date
              </label>
              <input
                type="date"
                value={pickupDate}
                onChange={(e) => setPickupDate(e.target.value)}
                data-testid="order-pickup-date"
                className="h-12 w-full rounded-xl border border-input bg-card px-3 text-sm"
              />
            </div>
          </div>
          {/* Manual admin orders — capture the origin ("Phone-in", "Walk-in", …)
              AND choose which kitchen fulfills it. Franchisees never see either
              field because their orders always come from their own franchise
              and route to the kitchen serving them. */}
          {user?.role === "admin" && (
            <>
              <div className="mt-4">
                <label className="label-eyebrow mb-2 block">Order Source (manual orders)</label>
                <input
                  value={orderSource}
                  onChange={(e) => setOrderSource(e.target.value)}
                  placeholder="e.g. Phone-in · Alice Chen, Walk-in, Instagram DM, Event"
                  data-testid="order-source"
                  className="h-12 w-full rounded-xl border border-input bg-card px-3 text-sm"
                />
              </div>
              {locations.length > 0 && (
                <div className="mt-4">
                  <label className="label-eyebrow mb-2 block">Fulfilled by kitchen</label>
                  <select
                    value={fulfillmentLocationId}
                    onChange={(e) => setFulfillmentLocationId(e.target.value)}
                    data-testid="order-fulfillment-location"
                    className="h-12 w-full rounded-xl border border-input bg-card px-3 text-sm"
                  >
                    <option value="">— pick a kitchen —</option>
                    {locations.filter((l) => l.is_warehouse).map((l) => (
                      <option key={l.id} value={l.id}>{l.name}</option>
                    ))}
                  </select>
                  <p className="text-[11px] text-muted-foreground mt-1.5">
                    Only employees at this location will see the order after approval.
                  </p>
                </div>
              )}
            </>
          )}

          {lastOrder && (
            <button
              type="button"
              onClick={duplicateLast}
              data-testid="duplicate-last-order-btn"
              className="mt-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
            >
              <RotateCcw className="h-4 w-4" /> Duplicate {lastOrder.order_no} ({lastOrder.customer || lastOrder.location_name})
            </button>
          )}
        </div>

        {/* Sections */}
        {grouped.map((section) => (
          <SectionCard
            key={section.category}
            section={section}
            qty={qty}
            setQty={setQty}
            subtotal={sectionTotals.sections[section.category]}
            onFlavorAdded={refreshItems}
          />
        ))}

        {/* Packaging — qty only, no prices */}
        {packagingItems.length > 0 && (
          <PackagingSection
            items={packagingItems}
            qty={pkgQty}
            setQty={setPkgQty}
          />
        )}

        {/* Notes */}
        <div className="luxury-card p-5 sm:p-6">
          <label className="label-eyebrow mb-2 block">Notes (optional)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Anything the kitchen should know"
            data-testid="order-notes"
            className="w-full rounded-xl border border-input bg-card px-3 py-2 text-sm"
          />
        </div>
      </div>

      {/* ───── Right: sticky grand total ───── */}
      <aside className="lg:sticky lg:top-6 h-fit space-y-3" data-testid="order-total-panel">
        <div className="luxury-card-elevated p-6">
          <div className="label-eyebrow mb-1">Grand Total</div>
          <div className="font-display text-4xl font-light tracking-tight tabular-nums mb-4"
               data-testid="order-grand-total">
            {fmtCurrency(sectionTotals.grand)}
          </div>
          <div className="text-xs text-muted-foreground mb-5">
            {linesCount} line{linesCount === 1 ? "" : "s"}
            {pickupDate && ` · pickup ${pickupDate}`}
          </div>

          {/* Full preview — every line the franchisee has added, with qty
              and price so they can review exactly what they're submitting
              before hitting the final button. */}
          <div className="space-y-1 mb-5 border-t border-border pt-4" data-testid="order-full-preview">
            {grouped.map((g) => {
              const sectionLines = g.items.filter((it) => (qty[it.id] || 0) > 0);
              if (sectionLines.length === 0) return null;
              return (
                <div key={g.category} className="mb-2">
                  <div className="flex items-baseline justify-between mb-1">
                    <span className="label-eyebrow text-[10px]">{g.label}</span>
                    <span className="tabular-nums text-xs text-muted-foreground" data-testid={`order-preview-section-total-${g.category}`}>
                      {sectionLines.reduce((s, it) => s + (qty[it.id] || 0), 0)} qty · {fmtCurrency(sectionTotals.sections[g.category] || 0)}
                    </span>
                  </div>
                  {sectionLines.map((it) => {
                    const q = qty[it.id] || 0;
                    const line = q * (it.price || 0);
                    return (
                      <div
                        key={it.id}
                        className="flex justify-between text-[11px] gap-2 pl-2"
                        data-testid={`order-preview-line-${it.id}`}
                      >
                        <span className="text-muted-foreground truncate pr-2">
                          <span className="tabular-nums text-foreground">{q}×</span> {it.name}
                        </span>
                        <span className="tabular-nums text-muted-foreground">{fmtCurrency(line)}</span>
                      </div>
                    );
                  })}
                </div>
              );
            })}
            {/* Packaging — prices are set by admin, so we show each line as a
                qty confirmation (not a $ amount) so the franchisee sees it
                was actually added. */}
            {(() => {
              const pkgLines = packagingItems
                .filter((it) => (pkgQty[it.name] || 0) > 0)
                .map((it) => ({ it, q: pkgQty[it.name] || 0 }));
              if (pkgLines.length === 0) return null;
              return (
                <div className="mb-2" data-testid="order-breakdown-packaging">
                  <div className="flex items-baseline justify-between mb-1">
                    <span className="label-eyebrow text-[10px]">Packaging</span>
                    <span className="text-[10px] text-muted-foreground">priced on invoice</span>
                  </div>
                  {pkgLines.map(({ it, q }) => (
                    <div
                      key={it.id}
                      className="flex justify-between text-[11px] gap-2 pl-2"
                      data-testid={`order-preview-pkg-${it.id}`}
                    >
                      <span className="text-muted-foreground truncate pr-2">
                        <span className="tabular-nums text-foreground">{q}×</span> {it.name}
                      </span>
                      <span className="tabular-nums text-muted-foreground">—</span>
                    </div>
                  ))}
                </div>
              );
            })()}
            {linesCount === 0 && (
              <div className="text-xs text-muted-foreground italic">
                No items added yet. Pick quantities above to see the preview.
              </div>
            )}
          </div>

          {err && (
            <div className="text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded-lg px-3 py-2 mb-3"
                 data-testid="order-error">
              {err}
            </div>
          )}

          <button
            type="button"
            onClick={submit}
            disabled={busy}
            data-testid="submit-order-btn"
            className="h-12 w-full rounded-xl bg-foreground text-background font-medium hover:bg-foreground/90 disabled:opacity-60 active:scale-[0.99]"
          >
            {busy ? "Submitting…" : "Submit Order"}
          </button>

          <div className="mt-5 pt-4 border-t border-border space-y-2">
            <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Prices subject to change.</p>
            <div className="text-[11px] text-muted-foreground leading-relaxed" data-testid="payment-terms">
              <div>Payment: <span className="text-foreground font-medium">Checks payable to Hafizo, LLC</span></div>
              <div>Terms: <span className="text-foreground font-medium">Net 30</span></div>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}

// ─── Section component ───
function SectionCard({ section, qty, setQty, subtotal, onFlavorAdded }) {
  const isEmpty = section.items.length === 0;
  // Detect if section has variable per-item pricing (gelato premium tiers)
  const prices = Array.from(new Set(section.items.map((i) => i.price)));
  const basePrice = isEmpty ? 0 : Math.min(...prices);

  const unitLabel = section.items[0]?.unit || "";
  const isSeasonal = section.category === "macaron_seasonal";
  const canAddFlavor = FLAVOR_ADDABLE_CATEGORIES.has(section.category);

  return (
    <section className="luxury-card overflow-hidden" data-testid={`section-${section.category}`}>
      <header className={`px-5 sm:px-6 py-4 border-b border-border ${isSeasonal ? "bg-[hsl(var(--accent-gold-soft))]/40" : "bg-secondary/30"}`}>
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              {isSeasonal && <Sparkles className="h-3.5 w-3.5 text-[hsl(var(--primary))]" />}
              <h2 className="font-display text-xl font-medium tracking-tight">{section.label}</h2>
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {isEmpty
                ? "No flavors in this category yet. Use the row below to add your first one."
                : section.category === "other"
                  ? "Priced per item"
                  : <>{unitLabel}{section.items.some((i) => i.premium)
                    ? ` · from ${fmtCurrency(basePrice)} (premium flavors marked)`
                    : ` · ${fmtCurrency(basePrice)} each`}</>}
            </div>
          </div>
          <div className="text-right">
            <div className="label-eyebrow">Subtotal</div>
            <div className="font-display text-lg font-medium tabular-nums" data-testid={`subtotal-${section.category}`}>
              {fmtCurrency(subtotal)}
            </div>
          </div>
        </div>
      </header>

      <div className="divide-y divide-border">
        {[...section.items].sort((a, b) => a.name.localeCompare(b.name)).map((it) => (
          <ItemRow
            key={it.id}
            item={it}
            quantity={qty[it.id] || 0}
            isPremium={!!it.premium}
            onChange={(q) => setQty((m) => ({ ...m, [it.id]: q }))}
          />
        ))}
      </div>
      {canAddFlavor && (
        <div className="px-5 sm:px-6 py-3 border-t border-border bg-secondary/20">
          <FlavorQuickAdd
            category={section.category}
            categoryLabel={section.label}
            onSaved={(created) => {
              // Immediately select the new flavor so the franchisee can
              // dial in a quantity without hunting for the row.
              if (created?.id) setQty((m) => ({ ...m, [created.id]: 1 }));
              onFlavorAdded?.();
            }}
            variant="row"
          />
        </div>
      )}
    </section>
  );
}

function ItemRow({ item, quantity, isPremium, onChange }) {
  const lineTotal = (item.price || 0) * (quantity || 0);
  const overStock = false;
  return (
    <div className={`flex items-center gap-3 px-5 sm:px-6 py-3.5 ${quantity > 0 ? "bg-[hsl(var(--accent-gold-soft))]/20" : ""}`}>
      {item.color && (
        <span
          className="h-7 w-7 rounded-full shrink-0 ring-1 ring-border"
          style={{ background: item.color }}
          aria-hidden
        />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium truncate">{item.name}</span>
          {item.category === "other" && item.unit && (
            <span className="text-[11px] text-muted-foreground" data-testid={`order-unit-${item.id}`}>per {item.unit}</span>
          )}
          {isPremium && (
            <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--primary))] bg-[hsl(var(--accent-gold-soft))] px-2 py-0.5 rounded-full">
              premium {fmtCurrency(item.price)}
            </span>
          )}
        </div>
      </div>
      <div className="text-xs text-muted-foreground tabular-nums w-16 text-right hidden sm:block">
        {fmtCurrency(lineTotal)}
      </div>
      <input
        type="number"
        min="0"
        value={quantity || ""}
        onChange={(e) => {
          const v = e.target.value;
          onChange(v === "" ? 0 : Math.max(0, parseInt(v, 10) || 0));
        }}
        placeholder="0"
        data-testid={`qty-${item.id}`}
        className={`h-11 w-16 rounded-xl border bg-card px-2 text-center text-base tabular-nums focus:ring-2 focus:ring-primary/30 ${overStock ? "border-destructive" : "border-input focus:border-primary"}`}
      />
    </div>
  );
}

// ─── Packaging — separate sheet, qty only, no prices ───
// Mirrors the Franchisee → My Inventory "Packaging Requests" table so a
// franchise owner sees the pack quantity for every item at a glance before
// picking a qty. See MyInventory.jsx for the same visual grammar.
function PackagingSection({ items, qty, setQty }) {
  const rows = [...items].sort((a, b) => a.name.localeCompare(b.name));
  return (
    <section className="luxury-card overflow-hidden" data-testid="section-packaging">
      <header className="px-5 sm:px-6 py-4 border-b border-border bg-secondary/30">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Package className="h-4 w-4 text-muted-foreground" />
            <h2 className="font-display text-xl font-medium tracking-tight">Packaging Re-order</h2>
            <span className="text-xs font-normal text-muted-foreground uppercase tracking-wider ml-1">
              {rows.length} item{rows.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>
        <div className="text-xs text-muted-foreground mt-1">{PACKAGING_NOTE}</div>
      </header>
      <table className="w-full text-sm">
        <thead className="bg-secondary/60">
          <tr className="text-left">
            <th className="px-5 py-3 label-eyebrow font-medium">Item</th>
            <th className="px-5 py-3 label-eyebrow font-medium">Pack</th>
            <th className="px-5 py-3 label-eyebrow font-medium text-right w-28">Order</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => {
            const selected = (qty[p.name] || 0) > 0;
            return (
              <tr
                key={p.id}
                className={`border-t border-border ${selected ? "bg-[hsl(var(--accent-gold-soft))]/20" : ""}`}
              >
                <td className="px-5 py-3">
                  <div className="font-medium flex items-center gap-2">
                    {p.name}
                  </div>
                </td>
                <td className="px-5 py-3 text-muted-foreground">
                  {p.pack_size ? (
                    <span className="inline-flex items-center gap-1.5">
                      <span className="font-medium text-foreground tabular-nums">{p.pack_size}</span>
                      <span className="text-xs">/ {p.unit}</span>
                    </span>
                  ) : p.unit}
                </td>
                <td className="px-5 py-3 text-right">
                  <input
                    type="number"
                    min="0"
                    value={qty[p.name] || ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      setQty((m) => ({ ...m, [p.name]: v === "" ? 0 : Math.max(0, parseInt(v, 10) || 0) }));
                    }}
                    placeholder="0"
                    data-testid={`pkg-qty-${p.id}`}
                    className="h-11 w-16 rounded-xl border border-input bg-card px-2 text-center text-base tabular-nums focus:border-primary focus:ring-2 focus:ring-primary/30"
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}
